/**
 * Client side of GET /search/results: the response types, the URL <-> filter
 * mapping, and where each kind of hit opens. Search itself is server-side only.
 */

export type SearchKind = "message" | "task" | "project" | "file" | "post" | "doc";

/** One run of snippet text; `hit` marks a matched term. Rendered as text, never as HTML. */
export type HighlightSegment = { text: string; hit: boolean };

export type SearchHit = {
  kind: SearchKind;
  id: string;
  score: number;
  createdAt: string;
  author: { id: string; displayName: string | null } | null;
  title: HighlightSegment[] | null;
  snippet: HighlightSegment[] | null;
  channel: { id: string; name: string | null } | null;
  project: { id: string; code: string | null; name: string | null } | null;
  parentMessageId: string | null;
  meta: string | null;
};

export type SearchResponse = { query: string; results: SearchHit[]; nextCursor: string | null };

export type SearchFilters = {
  q: string;
  type: SearchKind | "";
  person: string;
  channel: string;
  project: string;
  from: string;
  to: string;
  hasFile: boolean;
};

export const EMPTY_FILTERS: SearchFilters = {
  q: "",
  type: "",
  person: "",
  channel: "",
  project: "",
  from: "",
  to: "",
  hasFile: false,
};

const KINDS: SearchKind[] = ["message", "task", "project", "file", "post", "doc"];

export function filtersFromParams(params: URLSearchParams): SearchFilters {
  const type = params.get("type") ?? "";
  return {
    q: params.get("q") ?? "",
    type: (KINDS as string[]).includes(type) ? (type as SearchKind) : "",
    person: params.get("person") ?? "",
    channel: params.get("channel") ?? "",
    project: params.get("project") ?? "",
    from: params.get("from") ?? "",
    to: params.get("to") ?? "",
    hasFile: params.get("hasFile") === "true",
  };
}

/** Only the filters that are set, so URLs stay short and shareable. */
export function filtersToParams(filters: SearchFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q.trim()) params.set("q", filters.q.trim());
  for (const key of ["type", "person", "channel", "project", "from", "to"] as const) {
    if (filters[key]) params.set(key, filters[key]);
  }
  if (filters.hasFile) params.set("hasFile", "true");
  return params;
}

/** The text of a highlighted run list, without the markup. */
export const segmentsText = (segments: HighlightSegment[] | null): string =>
  segments ? segments.map((s) => s.text).join("") : "";

/**
 * Where a hit opens. A reply opens at its thread's root message, which is what
 * the chat page can scroll to; Chat handles the `?message=` jump.
 */
export function hitHref(hit: SearchHit): string {
  switch (hit.kind) {
    case "message":
      return `/chat/${hit.channel?.id ?? ""}?message=${hit.parentMessageId ?? hit.id}`;
    case "task":
      return `/work/task/${hit.id}`;
    case "project":
      return `/work/project/${hit.id}`;
    case "file":
      return `/files?q=${encodeURIComponent(segmentsText(hit.title))}`;
    case "post":
      return `/feed?post=${hit.id}`;
    case "doc":
      return `/work/doc/${hit.id}`;
  }
}

export const KIND_LABEL: Record<SearchKind, string> = {
  message: "Messages",
  task: "Tasks",
  project: "Projects",
  file: "Files",
  post: "Posts",
  doc: "Docs",
};

/** Next/previous index for arrow-key navigation, clamped at both ends. */
export function moveActive(current: number, delta: 1 | -1, count: number): number {
  if (count === 0) return -1;
  return Math.min(Math.max(current + delta, 0), count - 1);
}
